import { Platform } from '@angular/cdk/platform'
import { AfterViewInit, Component, ErrorHandler, Injector, OnDestroy, OnInit, ViewChild, ChangeDetectionStrategy, inject, signal } from '@angular/core'
import { FieldTree, form, required } from '@angular/forms/signals'
import { DateAdapter } from '@angular/material/core'
import { MatDatepicker } from '@angular/material/datepicker'
import { MatSort } from '@angular/material/sort'
import { MatTableDataSource } from '@angular/material/table'
import { DateService } from '@lemon/core/service/date.service'
import { OmaveroYhteisomyyntilaskuLahetetty } from '@lemon/lemonaid-lemonator-functions-and-lemonator-angular/model/omavero'
import { AsiakasService } from 'app/_angular/service/asiakas/asiakas.service'
import { DebugService } from 'app/_angular/service/debug.service'
import { FirebaseLemonator } from 'app/_angular/service/firebase-lemonator.service'
import { KirjautunutKayttajaService } from 'app/_angular/service/kirjautunut-kayttaja.service'
import { arvonMuutokset, asetaArvo } from 'app/_jaettu-angular/_lomakkeet/arvon-muutokset'
import { LemonKuukausiDateAdapter } from 'app/_jaettu-angular/_material/LemonKuukausiDateAdapter'
import { LadataanService } from 'app/_jaettu-angular/service/ladataan.service'
import { LemonTranslationService } from 'app/_jaettu-angular/service/lemon-translation.service'
import { Asiakas } from 'app/_jaettu-lemonator/model/asiakas'
import { KirjanpitoUriService } from 'app/_jaettu-lemonator/service/kirjanpito-uri.service'
import { Observable, Subject, combineLatest, startWith, switchMap, takeUntil } from 'rxjs'

interface YhteisomyyntiIlmoituksetLomake {
  status: string
  start: Date | null
  end: Date | null
}

@Component({
  templateUrl: './yhteisomyynti-ilmoitukset.component.html',
  styleUrls: ['./yhteisomyynti-ilmoitukset.component.css'],
  providers: [
    // `MomentDateAdapter` can be automatically provided by importing `MomentDateModule` in your
    // application's root module. We provide it at the component level here, due to limitations of
    // our example generation script.
    { provide: DateAdapter, useClass: LemonKuukausiDateAdapter, deps: [ErrorHandler, DateService, Platform, LemonTranslationService] },
  ],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class YhteisomyyntiIlmoituksetComponent implements OnInit, OnDestroy, AfterViewInit {
  asiakas: Asiakas = null
  readonly naytettavatKolumnit = signal<string[]>(['jakso', 'maaKoodi', 'alvTunnus', 'tavaramyynnit', 'palvelumyynnit', 'kolmikantamyynnit', 'lahetetty', 'status'])

  private _ngUnsubscribe: Subject<void> = new Subject<void>()
  dataSource = new MatTableDataSource<OmaveroYhteisomyyntilaskuLahetetty>([])
  ilmoituksetObservable: Observable<OmaveroYhteisomyyntilaskuLahetetty[]>
  hakulomake: FieldTree<YhteisomyyntiIlmoituksetLomake>
  private readonly _injector = inject(Injector)
  @ViewChild(MatSort, { static: true }) _sort: MatSort

  lahetyksenTila: { status: string }[] = [{ status: 'Onnistuneet' }, { status: 'Virheelliset' }, { status: 'Kaikki statukset' }]

  constructor(
    private _asiakasService: AsiakasService,
    public _dateService: DateService,
    private _firebaseLemonator: FirebaseLemonator,
    private _ladataanService: LadataanService,
    private _kirjautunutKayttajaService: KirjautunutKayttajaService,
    private _kirjanpitoUriService: KirjanpitoUriService,
    private _debugService: DebugService,
  ) {}

  ngOnInit(): void {
    this._kirjautunutKayttajaService.kirjanpitajaOnDevaajaObservable.pipe(takeUntil(this._ngUnsubscribe)).subscribe((onDevaaja: boolean) => {
      if (onDevaaja) {
        this.naytettavatKolumnit.update(kolumnit => [...kolumnit, 'debug'])
      }
    })

    const today = new Date()
    this.hakulomake = form(
      signal<YhteisomyyntiIlmoituksetLomake>({ status: '', start: this._dateService.lisaaKuukausia(today, -1), end: this._dateService.lisaaKuukausia(today, -1) }),
      polku => {
        required(polku.start)
        required(polku.end)
      },
      { injector: this._injector },
    )

    const start = this.hakulomake.start
    const end = this.hakulomake.end
    const status = this.hakulomake.status
    const muutokset = <T>(kentta: FieldTree<T>) => arvonMuutokset(kentta, { injector: this._injector })

    const statukset = {
      Onnistuneet: ['success'],
      Virheelliset: ['error'],
      'Kaikki statukset': ['success', 'error'],
    }

    combineLatest([
      muutokset(start).pipe(startWith(start().value())),
      muutokset(end).pipe(startWith(end().value())),
      muutokset(status).pipe(startWith(status().value())),
      this._asiakasService.nykyinenAsiakasAvainObservable,
    ])
      .pipe(
        switchMap(([startValue, endValue, statusValue, asiakas]) => {
          if (!startValue || !endValue || !statusValue || !asiakas) {
            return []
          }
          const listausUri = this._kirjanpitoUriService.annaOmaveroYhteisomyyntiIlmoitusLahetettyCollectionUri(asiakas.avain)
          const startNumberMonth = this._dateService.numberDateToNumberMonth(this._dateService.dateToNumber(startValue))
          const endNumberMonth = this._dateService.numberDateToNumberMonth(this._dateService.dateToNumber(endValue))

          return this._firebaseLemonator
            .firestoreCollection<OmaveroYhteisomyyntilaskuLahetetty>(listausUri)
            .orderBy('lahetetty', 'desc')
            .where('status', 'in', statukset[statusValue])
            .where('jakso', '>=', startNumberMonth)
            .where('jakso', '<=', endNumberMonth)
            .listen()
        }),
        takeUntil(this._ngUnsubscribe),
      )
      .subscribe(ilmoitukset => {
        this.dataSource.data = ilmoitukset
        this._ladataanService.lopetaLataaminen()
      })
    asetaArvo(status, this.lahetyksenTila[0].status)
  }

  ngAfterViewInit() {
    this.dataSource.sort = this._sort
  }

  chosenMonthHandlerStart(normalizedMonth: Date, datepicker: MatDatepicker<Date>) {
    asetaArvo(this.hakulomake.start, normalizedMonth)
    datepicker.close()
  }

  chosenMonthHandlerEnd(normalizedMonth: Date, datepicker: MatDatepicker<Date>) {
    asetaArvo(this.hakulomake.end, normalizedMonth)
    datepicker.close()
  }

  ngOnDestroy() {
    this._ngUnsubscribe.next()
    this._ngUnsubscribe.complete()
  }

  getDebugLink(asiakasAvain: string, lahetysAvain: string): string {
    return this._debugService.createFirestoreLink(this._kirjanpitoUriService.annaOmaveroYhteisomyyntiIlmoitusLahetettyDebugDataUri(asiakasAvain, lahetysAvain))
  }
}
